import { memo } from "react";
import { Box, Container, Typography, Button, Grid2 } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { RootState } from "../../Store/store";
import { setToggleAuthModal } from "../../Redux/authReducer";

// Assets
import heroImage from "./Assets/hero-maintenance.webp";
import housewifeImage from "./Assets/housewife-woking-home-lady-blue-shirt.webp";
import manCameraImage from "./Assets/man-holding-camera-home.webp";
import snowImage from "./Assets/full-length-man-skiing-snow-covered-field.webp";
import coupleImage from "./Assets/interracial-couple-getting-ready-move.webp";
import womanGlassImage from "./Assets/midsection-smiling-woman-holding-glass-home.webp";
import ctaImage from "./Assets/cta.webp";

interface MaintenanceSectionProps {
  title: string;
  items: string[];
  image: string;
  imageAlt: string;
  imagePosition: "left" | "right";
  backgroundColor?: string;
  onSignUpClick: () => void;
}

const MaintenanceSection = memo(
  ({
    title,
    items,
    image,
    imageAlt,
    imagePosition,
    backgroundColor = "transparent",
    onSignUpClick,
  }: MaintenanceSectionProps) => {
    const isImageLeft = imagePosition === "left";

    return (
      <Box sx={{ backgroundColor }}>
        <Grid2
          container
          rowSpacing={{ xs: 4, md: 6 }}
          sx={{
            flexDirection: {
              xs: "column",
              md: isImageLeft ? "row" : "row-reverse",
            },
            alignItems: "center",
          }}
        >
          {/* Image */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: isImageLeft ? "flex-start" : "flex-end",
              }}
            >
              <Box
                component="img"
                src={image}
                alt={imageAlt}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: "100%" },
                  height: "auto",
                //   borderRadius: "16px",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Grid2>

          {/* Text Content */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ px: { xs: 4, md: 8, lg: 20 }, py: { xs: 4, md: 6, lg: 8 } }}>
              <Typography
                variant="h5"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.35rem", md: "1.5rem" },
                  lineHeight: 1.3,
                  mb: { xs: 3, md: 4 },
                }}
              >
                {title}
              </Typography>

              <Box component="ul" sx={{ pl: 2, mb: { xs: 4, md: 5 } }}>
                {items.map((item, index) => (
                  <Box
                    component="li"
                    key={index}
                    sx={{
                      color: "#333",
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: 1.3,
                      py: 2,
                      fontWeight: 500,
                      borderBottom: index === items.length - 1 ? "none" : "1px solid #ddd",
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={onSignUpClick}
                sx={{
                  px: { xs: 5, md: 10, lg: 15 },
                  py: { xs: 1.5, md: 2, lg: 2.5 },
                  fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  color: "#ffffff",
                }}
              >
                Sign Up Now!
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    );
  }
);

const HomeMaintenanceGuide = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state?.auth || {}
  );

  const isLoggedIn =
    isAuthenticated &&
    user &&
    Object.keys(user || {}).length > 0;

  const handleSignUpClick = () => {
    if (isLoggedIn) {
      navigate("/");
    } else {
      dispatch(setToggleAuthModal(true));
    }
  };

  const sections = [
    {
      title: "Items that are vital:",
      items: [
        "Inspect roof and gutters for winter damage",
        "Clean out gutters and downspouts",
        "Service air conditioning system",
        "Check sprinklers and irrigation for leaks",
        "Power wash siding, walkways, and decks",
        "Inspect windows and screens; repair or replace if needed",
        "Test smoke and carbon monoxide detectors",
      ],
      image: manCameraImage,
      imageAlt: "Man inspecting home",
      
      imagePosition: "right" as const,
      backgroundColor: "#f2eded",
    },
    {
      title: "Things you should do over time:",
      items: [
        "Touch up exterior paint and check caulking around windows/doors",
        "Inspect and maintain landscaping; trim trees away from house",
        "Clean dryer vents and exhaust ducts",
        "Check for pests (termites, ants, rodents)",
        "Inspect and repair fencing or gates",
        "Service and clean outdoor furniture and grills",
        "Clean and reseal decks or patios if needed",
      ],
      image: womanGlassImage,
      imageAlt: "Woman doing home maintenance",
      
      imagePosition: "left" as const,
      backgroundColor: "transparent",
    },
    {
      title: "Rare but necessary maintenance items, you don't want to forget them.",
      items: [
        "Clean and inspect gutters before heavy rain",
        "Flush water heater to remove sediment buildup",
        "Inspect roof and flashing before winter",
        "Seal any cracks in concrete or foundation",
        "Schedule a chimney sweep if you have a fireplace",
        "Store or cover outdoor furniture and tools",
        "Test the heating system and change furnace filters",
      ],
      image: coupleImage,
      imageAlt: "Couple preparing home for winter",
      imagePosition: "right" as const,
      backgroundColor: "#f2eded",
    },
    {
      title: "Winter",
      items: [
        "Check for drafts and insulate windows/doors",
        "Run water in seldom-used pipes to prevent freezing",
        "Test sump pump and ensure proper drainage",
        "Inspect attic for signs of moisture or pests",
        "Keep pathways and driveways clear of ice/snow",
        "Rotate ceiling fans clockwise to circulate warm air",
        "Review and update your emergency preparedness supplies",
      ],
      image: snowImage,
      imageAlt: "Winter home maintenance",
      imagePosition: "left" as const,
      backgroundColor: "transparent",
    },
    {
      title: "Every 6 Months",
      items: [
        "Deep clean kitchen appliances (oven, fridge coils, range hood)",
        "Check water pressure and look for plumbing leaks",
        "Test and reset GFCI outlets",
        "Inspect crawl spaces and basements for moisture",
      ],
      image: housewifeImage,
      imageAlt: "Woman cleaning home",
      imagePosition: "right" as const,
      backgroundColor: "#f2eded",
    },
  ];

  return (
    <Container maxWidth="xl" disableGutters>
        <FullPaddingWrapper>
            {/* Hero Banner */}
            <Box
            sx={{
                position: "relative",
                width: "100%",
                minHeight: { xs: 300, sm: 350, md: 400 },
                backgroundImage: `url(${heroImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: { xs: "16px", md: "24px" },
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
            }}
            >
            {/* Teal overlay */}
            <Box
                sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "#004B4C",
                opacity: 0,
                mixBlendMode: "multiply",
                }}
            />

            {/* Hero Content */}
            <Box
                sx={{
                position: "relative",
                zIndex: 1,
                p: {
                    xl: "90px 100px",
                    lg: "80px 70px",
                    md: "50px 50px",
                    sm: "40px 30px",
                    xs: "30px 24px",
                },
                }}
            >
                <Typography
                variant="h3"
                sx={{
                    color: "#ffffff",
                    fontWeight: 600,
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem", lg: "3rem" },
                    lineHeight: 1,
                    mb: 2,
                    maxWidth: { xs: "100%", sm: "80%", md: 600 },
                }}
                >
                Your Year-Round Home Care Companion
                </Typography>

                <Typography
                variant="body1"
                sx={{
                    color: "#ffffff",
                    fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                    lineHeight: 1.5,
                    fontWeight: 300,
                    mb: 3,
                    py:{ xs: 2, md: 4, lg: 6 },
                    opacity: 0.95,
                    // maxWidth: 400,
                    maxWidth: { xs: "100%", sm: "80%", md: 450 },
                }}
                >
                Plan, prepare, and protect your home with seasonal maintenance
                advice, quick-fix solutions, and long-term improvement strategies.
                </Typography>

                <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleSignUpClick}
                sx={{
                    px: { xs: 5, md: 10, lg: 15 },
                    py: { xs: 1.5, md: 2, lg: 2.5 },
                    fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                    fontWeight: 500,
                    borderRadius: "8px",
                    textTransform: "none",
                    color: "#ffffff",
                }}
                >
                Sign Up Now!
                </Button>
            </Box>
            </Box>

            {/* Intro Text */}
            <Box sx={{ py: { xs: 5, md: 10, lg: 16 }, px: { xs: 2, md: 4 } }}>
            <Typography
                variant="body1"
                sx={{
                color: "#000000",
                fontSize: { xs: "1.1rem", md: "1.2rem", lg: "1.3rem" },
                lineHeight: 1.5,
                maxWidth: 900,
                margin: "0 auto",
                fontWeight: 500,
                }}
            >
                Owning a home requires ongoing attention to prevent costly repairs and preserve its value. Seasonal changes bring unique challenges that can affect everything from roofing and plumbing to landscaping and insulation. Year-round maintenance ensures your home stays safe, efficient, and comfortable in every season.
            </Typography>
            </Box>
        </FullPaddingWrapper>
        {/* Maintenance Sections */}
        <Box>
            {sections.map((section, index) => (
            <MaintenanceSection
                key={index}
                title={section.title}
                items={section.items}
                image={section.image}
                imageAlt={section.imageAlt}
                imagePosition={section.imagePosition}
                backgroundColor={section.backgroundColor}
                onSignUpClick={handleSignUpClick}
            />
            ))}
        </Box>
        <FullPaddingWrapper>
            {/* Bottom CTA Section */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    minHeight: { xs: 280, sm: 320, md: 380 },
                    backgroundImage: `url(${ctaImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: { xs: "16px", md: "24px" },
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    my: { xs: 4, md: 6 },
                }}
                >
                {/* Teal overlay */}
                <Box
                    sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "#004B4C",
                    opacity: 0,
                    mixBlendMode: "multiply",
                    }}
                />

                {/* CTA Content */}
                <Box
                    sx={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                    p: { xs: 3, md: 5 },
                    maxWidth: 600,
                    }}
                >
                    <Typography
                    variant="h4"
                    sx={{
                        color: "#ffffff",
                        fontWeight: 600,
                        fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.25rem" },
                        lineHeight: 1.3,
                        mb: 2,
                    }}
                    >
                    Start Maintaining Smarter
                    </Typography>

                    <Typography
                    variant="body1"
                    sx={{
                        color: "#ffffff",
                        fontSize: { xs: "0.95rem", md: "1rem", lg: "1.1rem" },
                        lineHeight: 1.5,
                        fontWeight: 300,
                        opacity: 0.9,
                        maxWidth: 400,
                        margin: "0 auto",
                        mb: 6,
                    }}
                    >
                    Join the movement toward a simpler, more powerful way to manage
                    your home.
                    </Typography>

                    <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={handleSignUpClick}
                    sx={{
                        px: { xs: 5, md: 10, lg: 15 },
                        py: { xs: 1.5, md: 2, lg: 2.5 },
                        fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                        fontWeight: 500,
                        borderRadius: "8px",
                        textTransform: "none",
                        color: "#ffffff",
                    }}
                    >
                    Sign Up Now!
                    </Button>
                </Box>
            </Box>
        </FullPaddingWrapper>

    </Container>
  );
});

export default HomeMaintenanceGuide;
